import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Alert, Badge, Button, Card, EmptyState, Skeleton } from '@/components/ui';
import { useAnnouncer } from '@/lib/a11y';
import { deleteAddress, setDefaultAddress, updateAddress, useAddresses } from '@/lib/api/addresses';
import { toApiError, type ApiError } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryClient';
import type { AddressOut } from '@/lib/api/types';
import type { PickedLocation } from '@/features/location/LocationPicker';
import type { AddressFormValues } from '@/lib/validators';

import { AddressEditor } from './AddressEditor';

/**
 * The address book: correct one, delete one, choose the default.
 *
 * Adding is deliberately absent. A new address is entered during checkout,
 * where the PIN code also prices the delivery — an "add address" button here
 * would either duplicate that flow without the pricing, or drag pricing into a
 * screen with no basket. The empty state says so rather than leaving a
 * customer hunting for a button that is not there.
 *
 * Deleting asks first. A saved address is typed once and reused for years, and
 * there is no undo: the row is gone and the next order means typing it again.
 * Orders already placed keep their own address snapshot, so deleting one never
 * rewrites history — which is worth telling the customer, because the fear of
 * that is exactly what stops people tidying up.
 */
export function SavedAddresses() {
  const queryClient = useQueryClient();
  const { announce } = useAnnouncer();
  const addresses = useAddresses(true);

  const [editing, setEditing] = useState<AddressOut | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.addresses });

  const save = useMutation({
    mutationFn: ({
      id,
      values,
      location,
    }: {
      id: string;
      values: AddressFormValues;
      location: PickedLocation | null;
    }) =>
      updateAddress(id, {
        ...values,
        // Sent whenever the editor has one, so an address saved before we
        // started asking gains a pin the first time it is edited — which is
        // the route out of the refusal checkout gives it.
        ...(location
          ? {
              latitude: location.lat,
              longitude: location.lng,
              location_source: location.source,
              ...(location.accuracyM !== null ? { location_accuracy_m: location.accuracyM } : {}),
            }
          : {}),
        // Empty means "none", which the API models as null. Sending "" would
        // store a blank string and print an empty line on the label.
        line2: values.line2?.trim() ? values.line2 : null,
        // Explicitly cleared, not omitted. The editor merges any existing
        // landmark into the single box, so leaving the column set would put
        // the same text on the label twice.
        landmark: null,
      }),
    onSuccess: async () => {
      setEditing(null);
      setActionError(null);
      await invalidate();
      announce('Address updated.');
    },
    onError: (error) => setActionError(toApiError(error)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteAddress(id),
    onSuccess: async () => {
      setConfirmingDelete(null);
      setActionError(null);
      await invalidate();
      announce('Address deleted.');
    },
    onError: (error) => setActionError(toApiError(error)),
  });

  const makeDefault = useMutation({
    mutationFn: (id: string) => setDefaultAddress(id),
    onSuccess: async () => {
      setActionError(null);
      await invalidate();
      announce('Default address changed.');
    },
    onError: (error) => setActionError(toApiError(error)),
  });

  if (addresses.isPending) {
    return (
      <Card title="Saved addresses">
        <div className="flex flex-col gap-3" aria-busy="true">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </Card>
    );
  }

  if (addresses.isError) {
    return (
      <Card title="Saved addresses">
        <Alert severity="danger" title="We could not load your addresses">
          <p>{addresses.error.message}</p>
          <Button variant="secondary" onClick={() => void addresses.refetch()} className="mt-3">
            Try again
          </Button>
        </Alert>
      </Card>
    );
  }

  const items = addresses.data?.items ?? [];

  return (
    <Card title="Saved addresses">
      {/* Mutation failures surface here rather than in the modal, because a
          delete and a make-default have no modal of their own. */}
      {actionError && Object.keys(actionError.fieldErrors).length === 0 ? (
        <Alert severity="danger" title="That did not work" className="mb-3">
          <p>{actionError.message}</p>
        </Alert>
      ) : null}

      {items.length === 0 ? (
        <EmptyState
          glyph="📮"
          title="No saved addresses yet"
          description="Addresses are saved when you place an order, so the next one is quicker."
        />
      ) : (
        // eslint-disable-next-line jsx-a11y/no-redundant-roles -- Safari/VoiceOver drops list semantics when list-style is none; see Stepper.
        <ul className="flex list-none flex-col gap-3" role="list">
          {items.map((address) => (
            <li
              key={address.id}
              // Actions BELOW the address, not beside it. The content column is
              // capped at a reading width (32rem), and three buttons alongside
              // squeezed a two-line address into four ragged lines — visibly
              // worse on the row with the most actions, which is every row that
              // is not already the default.
              className="rounded-card border border-line p-3"
            >
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 font-medium text-ink">
                  {address.name}
                  {address.is_default ? <Badge tone="brand">Default</Badge> : null}
                </p>
                <address className="mt-1 text-sm not-italic text-ink-muted">
                  {[address.line1, address.line2, address.landmark, address.city, address.state]
                    .filter(Boolean)
                    .join(', ')}
                  <br />
                  {address.pincode} &middot; {address.phone}
                </address>

                {confirmingDelete === address.id ? (
                  <Alert severity="warning" title="Delete this address?" className="mt-3">
                    <p>
                      It will not change any order you have already placed &mdash; those keep the
                      address they were sent to.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        variant="danger"
                        onClick={() => remove.mutate(address.id)}
                        loading={remove.isPending}
                      >
                        Yes, delete it
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => setConfirmingDelete(null)}
                        disabled={remove.isPending}
                      >
                        Keep it
                      </Button>
                    </div>
                  </Alert>
                ) : null}
              </div>

              {confirmingDelete === address.id ? null : (
                <div className="mt-3 flex flex-wrap gap-2 border-t border-line pt-3">
                  {address.is_default ? null : (
                    <Button
                      variant="secondary"
                      onClick={() => makeDefault.mutate(address.id)}
                      loading={makeDefault.isPending && makeDefault.variables === address.id}
                    >
                      Make default
                    </Button>
                  )}
                  <Button variant="secondary" onClick={() => setEditing(address)}>
                    Edit
                  </Button>
                  <Button variant="secondary" onClick={() => setConfirmingDelete(address.id)}>
                    Delete
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {editing ? (
        <AddressEditor
          open
          address={editing}
          onClose={() => {
            setEditing(null);
            setActionError(null);
          }}
          onSave={(values, location) => save.mutate({ id: editing.id, values, location })}
          saving={save.isPending}
          error={actionError}
        />
      ) : null}
    </Card>
  );
}
